import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown, Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import {
  fetchEmployeeCampaigns,
  fetchEmployeeDesignations,
  fetchEmployeeDepartments,
  fetchAppraisalStatuses,
} from "actions";
import { map } from "lodash";
import { EMPTY_APPRAISAL_FILTERS } from "../../utils/appraisalListQuery";

const QUARTER_FILTER_OPTIONS = [
  { value: "Q1", label: "Q1" },
  { value: "Q2", label: "Q2" },
  { value: "Q3", label: "Q3" },
  { value: "Q4", label: "Q4" },
];

const MY_APPRAISALS_LIST_OPTIONS = [
  { value: "approval_pending", label: "Approval Pending" },
  { value: "scoring_pending", label: "Scoring Pending" },
];

const toFilterOption = (option) =>
  option ? { value: option.value, label: option.label } : null;

const Filter = ({
  filters = EMPTY_APPRAISAL_FILTERS,
  onFiltersChange,
  showQuarterlyFilters = false,
  loadFilterOptions = false,
}) => {
  const dispatch = useDispatch();
  const employeeState = useSelector((state) => state.employeeState);
  const appraisalProgressList = useSelector((state) => state.appraisalState?.appraisalProgressList || []);

  const [show, setShow] = useState(false);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [monthOptions, setMonthOptions] = useState([]);
  const [pafStatusOptions, setPafStatusOptions] = useState([]);
  const [supervisorOptions, setSupervisorOptions] = useState([]);

  const selectedCampaign = filters?.campaign || null;
  const selectedDesignation = filters?.designation || null;
  const selectedDepartment = filters?.department || null;
  const selectedMonth = filters?.month || null;
  const selectedQuarter = filters?.quarter || null;
  const selectedPafStatus = filters?.pafStatus || null;
  const selectedSupervisor = filters?.supervisor || null;
  const selectedMyAppraisalsList = filters?.myAppraisalsList || null;

  useEffect(() => {
    const currentMonthIndex = new Date().getMonth();
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const options = monthNames.slice(0, currentMonthIndex + 1).map((month) => ({
      value: month,
      label: month,
    }));

    setMonthOptions(options);
  }, []);

  useEffect(() => {
    if (employeeState.campaignsList && employeeState.campaignsList.length > 0) {
      const options = map(employeeState.campaignsList, (campaign) => ({
        value: campaign.id,
        label: campaign.name || campaign.client_name || campaign.campaign_name || "",
      }));
      setCampaignOptions(options);
    }
  }, [employeeState.campaignsList]);

  useEffect(() => {
    if (employeeState.designationsList && employeeState.designationsList.length > 0) {
      const options = map(employeeState.designationsList, (designation) => ({
        value: designation.id,
        label: designation.name || designation.designation_name || "",
      }));
      setDesignationOptions(options);
    }
  }, [employeeState.designationsList]);

  useEffect(() => {
    if (employeeState.departmentsList && employeeState.departmentsList.length > 0) {
      const options = map(employeeState.departmentsList, (department) => ({
        value: department.id,
        label: department.name || department.department_name || "",
      }));
      setDepartmentOptions(options);
    }
  }, [employeeState.departmentsList]);

  useEffect(() => {
    if (!Array.isArray(employeeState.appraisalStatusesList) || employeeState.appraisalStatusesList.length === 0) {
      setPafStatusOptions([]);
      return;
    }

    const toLabel = (value) =>
      String(value)
        .trim()
        .replace(/_/g, " ")
        .replace(/\s+/g, " ")
        .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());

    const statusMap = new Map();
    employeeState.appraisalStatusesList.forEach((item) => {
      const rawValue =
        (typeof item === "string" && item) ||
        item?.status ||
        item?.name ||
        item?.label ||
        item?.value ||
        "";
      const statusValue = String(rawValue).trim();
      if (!statusValue) return;
      statusMap.set(statusValue.toLowerCase(), {
        value: statusValue,
        label: toLabel(statusValue),
      });
    });

    setPafStatusOptions(Array.from(statusMap.values()).sort((a, b) => a.label.localeCompare(b.label)));
  }, [employeeState.appraisalStatusesList]);

  useEffect(() => {
    if (!Array.isArray(appraisalProgressList) || appraisalProgressList.length === 0) {
      setSupervisorOptions([]);
      return;
    }

    const supervisorMap = new Map();
    const getSupervisorDisplayName = (value) => {
      if (!value) return "";
      if (typeof value === "string") return value.trim();
      if (Array.isArray(value)) {
        for (const item of value) {
          const candidate = getSupervisorDisplayName(item);
          if (candidate) return candidate;
        }
        return "";
      }
      if (typeof value === "object") {
        const nameParts = [value.firstname, value.middlename, value.lastname]
          .map((part) => (part == null ? "" : String(part).trim()))
          .filter(Boolean);
        if (nameParts.length > 0) return nameParts.join(" ");
        return String(
          value.full_name ||
            value.name ||
            value.employee_name ||
            value.supervisor_name ||
            value.label ||
            ""
        ).trim();
      }
      return "";
    };

    appraisalProgressList.forEach((row) => {
      const supervisorName =
        getSupervisorDisplayName(row?.supervisor_name) ||
        getSupervisorDisplayName(row?.supervisor) ||
        getSupervisorDisplayName(row?.employee?.supervisor_name) ||
        getSupervisorDisplayName(row?.employee?.supervisor);
      if (!supervisorName) return;
      supervisorMap.set(supervisorName.toLowerCase(), {
        value: supervisorName,
        label: supervisorName,
      });
    });

    setSupervisorOptions(Array.from(supervisorMap.values()).sort((a, b) => a.label.localeCompare(b.label)));
  }, [appraisalProgressList]);

  const ensureFilterOptionsLoaded = () => {
    const campaignsLoaded = (employeeState.campaignsList || []).length > 0;
    const designationsLoaded = (employeeState.designationsList || []).length > 0;
    const departmentsLoaded = (employeeState.departmentsList || []).length > 0;
    const appraisalStatusesLoaded = (employeeState.appraisalStatusesList || []).length > 0;

    if (!campaignsLoaded && !employeeState.isCampaignsLoading) {
      dispatch(fetchEmployeeCampaigns());
    }
    if (!designationsLoaded && !employeeState.isDesignationsLoading) {
      dispatch(fetchEmployeeDesignations());
    }
    if (!departmentsLoaded && !employeeState.isDepartmentsLoading) {
      dispatch(fetchEmployeeDepartments());
    }
    if (!appraisalStatusesLoaded && !employeeState.isAppraisalStatusesLoading) {
      dispatch(fetchAppraisalStatuses());
    }
  };

  useEffect(() => {
    if (!loadFilterOptions) return;
    ensureFilterOptionsLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFilterOptions]);

  const handleToggle = (isOpen) => {
    if (isOpen) {
      ensureFilterOptionsLoaded();
    }
    setShow(isOpen);
  };

  const updateFilter = (key, option) => {
    if (!onFiltersChange) return;
    onFiltersChange({
      ...filters,
      [key]: toFilterOption(option),
    });
  };

  const handleClear = () => {
    if (!onFiltersChange) return;
    onFiltersChange({ ...EMPTY_APPRAISAL_FILTERS });
  };

  return (
    <Dropdown
      show={show}
      onToggle={handleToggle}
      align="end"
      className="filters-dropdown"
    >
      <Dropdown.Toggle variant="light" className="d-flex align-items-center gap-2">
        <img src="/assets/images/filter-icon.svg" alt="" /> Filters
      </Dropdown.Toggle>

      <Dropdown.Menu className="p-4" style={{ minWidth: "750px" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="m-0 font-20">Filters</h5>
          <button
            type="button"
            className="text-success p-0 border-0 bg-transparent"
            onClick={handleClear}
          >
            Clear
          </button>
        </div>

        <hr />

        <Form>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Label className="label-input">Campaign</Form.Label>
              <Select
                placeholder="Campaign"
                className="multi-select w-100"
                classNamePrefix="react-select"
                isSearchable
                options={campaignOptions}
                value={selectedCampaign}
                onChange={(option) => updateFilter("campaign", option)}
                isClearable
              />
            </Col>
            <Col md={4}>
              <Form.Label className="label-input">Designation</Form.Label>
              <Select
                placeholder="Designation"
                className="multi-select w-100"
                classNamePrefix="react-select"
                isSearchable
                options={designationOptions}
                value={selectedDesignation}
                onChange={(option) => updateFilter("designation", option)}
                isClearable
              />
            </Col>
            <Col md={4}>
              <Form.Label className="label-input">Department</Form.Label>
              <Select
                placeholder="Department"
                className="multi-select w-100"
                classNamePrefix="react-select"
                isSearchable
                options={departmentOptions}
                value={selectedDepartment}
                onChange={(option) => updateFilter("department", option)}
                isClearable
              />
            </Col>
          </Row>

          {showQuarterlyFilters ? (
            <Row className="mb-3">
              <Col md={4}>
                <Form.Label className="label-input">Quarterly</Form.Label>
                <Select
                  placeholder="Select quarter"
                  className="multi-select w-100"
                  classNamePrefix="react-select"
                  isSearchable={false}
                  options={QUARTER_FILTER_OPTIONS}
                  value={selectedQuarter}
                  onChange={(option) => updateFilter("quarter", option)}
                  isClearable
                />
              </Col>
              <Col md={4}>
                <Form.Label className="label-input">Month</Form.Label>
                <Select
                  placeholder="Select month"
                  className="multi-select w-100"
                  classNamePrefix="react-select"
                  isSearchable
                  options={monthOptions}
                  value={selectedMonth}
                  onChange={(option) => updateFilter("month", option)}
                  isClearable
                />
              </Col>
              <Col md={4}>
                <Form.Label className="label-input">My Appraisals list</Form.Label>
                <Select
                  placeholder="My Appraisals list"
                  className="multi-select w-100"
                  classNamePrefix="react-select"
                  isSearchable={false}
                  options={MY_APPRAISALS_LIST_OPTIONS}
                  value={selectedMyAppraisalsList}
                  onChange={(option) => updateFilter("myAppraisalsList", option)}
                  isClearable
                />
              </Col>
            </Row>
          ) : (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label className="label-input">Month</Form.Label>
                  <Select
                    placeholder="Select month"
                    className="multi-select w-100"
                    classNamePrefix="react-select"
                    isSearchable
                    options={monthOptions}
                    value={selectedMonth}
                    onChange={(option) => updateFilter("month", option)}
                    isClearable
                  />
                </Col>
                <Col md={4}>
                  <Form.Label className="label-input">PAF Status</Form.Label>
                  <Select
                    placeholder="Select PAF Status"
                    className="multi-select w-100"
                    classNamePrefix="react-select"
                    isSearchable
                    options={pafStatusOptions}
                    value={selectedPafStatus}
                    onChange={(option) => updateFilter("pafStatus", option)}
                    isClearable
                  />
                </Col>
                <Col md={4}>
                  <Form.Label className="label-input">Supervisor</Form.Label>
                  <Select
                    placeholder="Select Supervisor"
                    className="multi-select w-100"
                    classNamePrefix="react-select"
                    isSearchable
                    options={supervisorOptions}
                    value={selectedSupervisor}
                    onChange={(option) => updateFilter("supervisor", option)}
                    isClearable
                  />
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label className="label-input">My Appraisals list</Form.Label>
                  <Select
                    placeholder="My Appraisals list"
                    className="multi-select w-100"
                    classNamePrefix="react-select"
                    isSearchable={false}
                    options={MY_APPRAISALS_LIST_OPTIONS}
                    value={selectedMyAppraisalsList}
                    onChange={(option) => updateFilter("myAppraisalsList", option)}
                    isClearable
                  />
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default Filter;
